import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Package, Trash2, Upload, X, Ban, CheckCircle, Search } from 'lucide-react';

import { useAuth, API, getImageUrl } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { compressImage } from '../../utils/imageCompressor';
import { formatAmount, formatPrice } from '../../utils/format';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
const getErrorMessage = (error, defaultMsg) => {
  const errorDetail = error.response?.data?.detail;
  if (typeof errorDetail === 'string') return errorDetail;
  if (Array.isArray(errorDetail)) return errorDetail[0]?.msg || defaultMsg;
  return defaultMsg;
};

const ProductsTab = () => {
  const { getAuthHeaders } = useAuth();
  const { hasPermission } = usePermissions();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState(''); // búsqueda por nombre o código de barras
  const [formData, setFormData] = useState({
    category_id: '',
    name: '',
    barcode: '',
    description: '',
    price: 0,
    stock: '',
    image_url: '',
    preparation_time: 15,
    is_available: true,
    ingredients: [],
    variants: []  // Nueva propiedad para variantes
  });
  const [newIngredient, setNewIngredient] = useState('');
  const [newVariant, setNewVariant] = useState({ name: '', price: '' }); // Estado para nueva variante (precio como texto)
  const [editingVariantId, setEditingVariantId] = useState(null); // variante en edición
  const [variantDraft, setVariantDraft] = useState({ name: '', price: '' }); // valores nuevos de esa variante
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categories`, { headers: getAuthHeaders() })
      ]);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
    } catch {
      toast.error('Error al cargar datos');
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Comprimir en el navegador (redimension a 800x800 + WebP): se acepta
    // cualquier imagen sin tope de tamano porque llega ya optimizada al backend.
    const compressedFile = await compressImage(file);
    setImageFile(compressedFile);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(compressedFile);
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();

    // Bloqueo: ningún precio de venta (base o de variante) puede quedar por debajo
    // del costo de inventario ya guardado. El costo se gestiona en Inventario, no en
    // esta ficha; al crear aún no existe costo, así que la regla aplica al actualizar.
    const invCost = editingProduct && Number.isFinite(Number(editingProduct.cost)) ? Number(editingProduct.cost) : 0;
    if (invCost > 0) {
      const sellingPrices = [Number(formData.price), ...(formData.variants || []).map((v) => Number(v.price))]
        .filter((pr) => Number.isFinite(pr) && pr >= 0);
      if (sellingPrices.some((pr) => pr < invCost)) {
        toast.error(`No se puede guardar: hay un precio de venta inferior al costo de inventario ($${formatAmount(invCost)}). Corrige el precio antes de continuar.`);
        return;
      }
    }

    setLoading(true);

    try {
      let finalImageUrl = formData.image_url;

      // Upload image if file selected
      if (imageFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', imageFile);

        const uploadRes = await axios.post(
          `${API}/products/upload-image`,
          formDataUpload,
          {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        finalImageUrl = uploadRes.data.image_url;
      }

      const productData = {
        ...formData,
        image_url: finalImageUrl
      };

      // Existencia opcional: solo se envía si es numérica (evita 422 y upserts vacíos).
      // El costo ya no se edita en esta ficha (se gestiona en Inventario), así que no se envía.
      const rawStock = productData.stock;
      const hasStock = rawStock !== '' && rawStock != null && Number.isFinite(Number(rawStock));
      if (hasStock) productData.stock = Math.max(0, Math.round(Number(rawStock)));
      else delete productData.stock;

      if (editingProduct) {
        // Update existing product
        await axios.put(`${API}/products/${editingProduct.id}`, productData, {
          headers: getAuthHeaders()
        });
        toast.success('Producto actualizado exitosamente');
      } else {
        // Create new product
        await axios.post(`${API}/products`, productData, {
          headers: getAuthHeaders()
        });
        toast.success('Producto creado exitosamente');
      }
      
      await loadData();
      handleCloseDialog();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar producto'));
    }

    setLoading(false);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      category_id: product.category_id,
      name: product.name,
      barcode: product.barcode || '',
      description: product.description || '',
      price: product.price,
      stock: product.stock ?? '',
      image_url: product.image_url || '',
      preparation_time: product.preparation_time || 15,
      is_available: product.is_available !== false,
      ingredients: product.ingredients || [],
      variants: product.variants || []  // Agregado: cargar variantes
    });
    setImagePreview(product.image_url || null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      setEditingProduct(null);
      setFormData({ category_id: '', name: '', barcode: '', description: '', price: 0, stock: '', image_url: '', preparation_time: 15, is_available: true, ingredients: [], variants: [] });
      setImageFile(null);
      setImagePreview(null);
      setNewIngredient('');
      setNewVariant({ name: '', price: '' });
      setEditingVariantId(null);
    }, 100);
  };

  const handleOpenDialog = () => {
    setEditingProduct(null);
    setFormData({ category_id: '', name: '', barcode: '', description: '', price: 0, stock: '', image_url: '', preparation_time: 15, is_available: true, ingredients: [], variants: [] });
    setImageFile(null);
    setImagePreview(null);
    setNewIngredient('');
    setNewVariant({ name: '', price: '' });
    setEditingVariantId(null);
    setIsDialogOpen(true);
  };

  const toggleAvailability = async (product) => {
    try {
      await axios.patch(
        `${API}/products/${product.id}/toggle-availability`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Producto ${product.is_available ? 'deshabilitado' : 'habilitado'}`);
      loadData();
    } catch {
      toast.error('Error al cambiar disponibilidad');
    }
  };

  const handleAddIngredient = () => {
    if (!newIngredient.trim()) {
      toast.error('Ingresa un ingrediente válido');
      return;
    }
    const ingredients = formData.ingredients || [];
    if (ingredients.includes(newIngredient.trim())) {
      toast.error('Este ingrediente ya está en la lista');
      return;
    }
    setFormData({
      ...formData,
      ingredients: [...ingredients, newIngredient.trim()]
    });
    setNewIngredient('');
  };

  const handleRemoveIngredient = (index) => {
    setFormData({
      ...formData,
      ingredients: (formData.ingredients || []).filter((_, i) => i !== index)
    });
  };

  // Funciones para manejar variantes
  const handleAddVariant = () => {
    if (!newVariant.name.trim()) {
      toast.error('Ingresa un nombre para la variante');
      return;
    }
    // El precio se captura como texto libre: se valida y convierte aquí
    const parsedPrice = parseFloat(newVariant.price);
    if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      toast.error('El precio debe ser mayor a 0');
      return;
    }
    
    const variant = {
      id: `var-${Date.now()}`,
      name: newVariant.name.trim(),
      price: parsedPrice,
      is_available: true
    };
    
    setFormData({
      ...formData,
      variants: [...(formData.variants || []), variant]
    });
    setNewVariant({ name: '', price: '' });
    toast.success('Variante agregada');
  };

  const handleRemoveVariant = (variantId) => {
    setFormData({
      ...formData,
      variants: (formData.variants || []).filter(v => v.id !== variantId)
    });
  };

  const handleToggleVariantAvailability = (variantId) => {
    setFormData({
      ...formData,
      variants: (formData.variants || []).map(v => 
        v.id === variantId ? { ...v, is_available: !v.is_available } : v
      )
    });
  };

  // Editar una variante ya creada (nombre y precio) en vez de borrarla y volverla a añadir
  const handleStartEditVariant = (variant) => {
    setEditingVariantId(variant.id);
    setVariantDraft({ name: variant.name || '', price: String(variant.price ?? '') });
  };

  const handleCancelEditVariant = () => {
    setEditingVariantId(null);
    setVariantDraft({ name: '', price: '' });
  };

  const handleSaveEditVariant = (variantId) => {
    const name = (variantDraft.name || '').trim();
    const price = parseFloat(variantDraft.price);
    if (!name) {
      toast.error('El nombre de la variante no puede quedar vacío');
      return;
    }
    if (!Number.isFinite(price) || price <= 0) {
      toast.error('El precio debe ser mayor a 0');
      return;
    }
    if ((formData.variants || []).some(v => v.id !== variantId && (v.name || '').trim().toLowerCase() === name.toLowerCase())) {
      toast.error(`Ya existe una variante llamada "${name}"`);
      return;
    }
    setFormData({
      ...formData,
      variants: (formData.variants || []).map(v =>
        v.id === variantId ? { ...v, name, price } : v
      )
    });
    handleCancelEditVariant();
    toast.success('Variante actualizada');
  };

  const filteredProducts = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    const term = searchTerm.trim().toLowerCase();
    return list.filter((p) => {
      if (selectedCategoryFilter && p.category_id !== selectedCategoryFilter) return false;
      if (!term) return true;
      return (
        (p.name || '').toLowerCase().includes(term) ||
        (p.barcode || '').toLowerCase().includes(term)
      );
    });
  }, [products, selectedCategoryFilter, searchTerm]);

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) {
      return;
    }

    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Producto eliminado exitosamente');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar producto'));
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/categories`, categoryFormData, {
        headers: getAuthHeaders()
      });
      toast.success('Categoría creada exitosamente');
      setIsCategoryDialogOpen(false);
      setCategoryFormData({ name: '', description: '' });
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al crear categoría'));
    }

    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Productos</h2>
        <div className="flex gap-2">
          <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-xl border-orange-200">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Categoría
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crear Nueva Categoría</DialogTitle>
              <DialogDescription>
                Crea una nueva categoría para organizar tus productos
              </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCategory} className="space-y-4">
                <div>
                  <Label>Nombre</Label>
                  <Input
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                    required
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Textarea
                    value={categoryFormData.description}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                  {loading ? 'Creando...' : 'Crear Categoría'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            {/* Botón Nuevo Producto - Solo si tiene permiso de crear */}
            {hasPermission('products:create') && (
              <DialogTrigger asChild>
                <Button 
                  onClick={handleOpenDialog}
                  data-testid="create-product-button" 
                  className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Producto
                </Button>
              </DialogTrigger>
            )}
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Editar Producto' : 'Crear Nuevo Producto'}</DialogTitle>
                <DialogDescription>
                  {editingProduct
                    ? 'Modifica los datos del producto seleccionado'
                    : 'Completa los datos para agregar un nuevo producto al menú'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateProduct} className="space-y-4">
                <div>
                  <Label>Categoría</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                    required
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Selecciona categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nombre</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Código de Barras</Label>
                  <Input
                    type="text"
                    value={formData.barcode}
                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                    placeholder="Escanea o escribe el código (opcional)"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Imagen del Producto</Label>
                  <div className="space-y-3">
                    {/* File upload input */}
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="rounded-xl"
                        id="image-upload"
                      />
                      <Label htmlFor="image-upload" className="cursor-pointer">
                        <div className="px-4 py-2 bg-orange-100 text-orange-700 rounded-xl hover:bg-orange-200 transition-colors flex items-center gap-2">
                          <Upload className="w-4 h-4" />
                          Subir
                        </div>
                      </Label>
                    </div>

                    {/* Image preview */}
                    {(imagePreview || formData.image_url) && (
                      <div className="relative w-full h-48 bg-gray-100 rounded-xl border-2 border-orange-200 overflow-hidden">
                        <img 
                          src={imagePreview || getImageUrl(formData.image_url)}
                          alt="Preview" 
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x300?text=Error+al+cargar+imagen';
                          }}
                        />
                        {imagePreview && (
                          <div className="absolute top-2 right-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setImageFile(null);
                                setImagePreview(null);
                              }}
                              className="rounded-full w-8 h-8 p-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      Formatos soportados: JPG, PNG, GIF. Tamaño máximo: 5MB
                    </p>
                  </div>
                </div>
                <div>
                  <Label>Precio ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    required
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Existencia (opcional)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    placeholder="0"
                    className="rounded-xl"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Si indicas existencia, el producto activará el control de inventario y se ocultará en el POS cuando llegue a 0. El costo se gestiona en Inventario.
                </p>
                <div>
                  <Label>Tiempo de Preparación (min)</Label>
                  <Input
                    type="number"
                    value={formData.preparation_time}
                    onChange={(e) => setFormData({ ...formData, preparation_time: parseInt(e.target.value) })}
                    className="rounded-xl"
                  />
                </div>

                {/* Ingredientes Section */}
                <div className="space-y-3">
                  <Label>Ingredientes</Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={newIngredient}
                      onChange={(e) => setNewIngredient(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddIngredient();
                        }
                      }}
                      placeholder="Ej: Tomate, Lechuga, Queso..."
                      className="rounded-xl flex-1"
                    />
                    <Button
                      type="button"
                      onClick={handleAddIngredient}
                      variant="outline"
                      className="rounded-xl"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {formData.ingredients && formData.ingredients.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl max-h-32 overflow-y-auto">
                      {formData.ingredients.map((ingredient, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 bg-white px-3 py-1 rounded-lg border border-gray-200"
                        >
                          <span className="text-sm">{ingredient}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveIngredient(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">Agrega ingredientes uno por uno presionando Enter o el botón +</p>
                </div>

                {/* Variantes Section */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Variantes de Precio</Label>
                    <span className="text-xs text-gray-500">
                      {(formData.variants && formData.variants.length > 0) ? `${formData.variants.length} variante(s)` : 'Opcional'}
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={newVariant.name}
                      onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })}
                      placeholder="Nombre (ej: Grande, BBQ Picante...)"
                      className="rounded-xl flex-1"
                    />
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={newVariant.price}
                      onChange={(e) => setNewVariant({ ...newVariant, price: e.target.value })}
                      placeholder="Precio"
                      className="rounded-xl w-28"
                    />
                    <Button
                      type="button"
                      onClick={handleAddVariant}
                      variant="outline"
                      className="rounded-xl"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {formData.variants && formData.variants.length > 0 && (
                    <div className="space-y-2 p-3 bg-orange-50 rounded-xl max-h-48 overflow-y-auto">
                      {formData.variants.map((variant) => (
                        <div
                          key={variant.id}
                          className="bg-white px-3 py-2 rounded-lg border border-orange-200"
                        >
                          {editingVariantId === variant.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                type="text"
                                value={variantDraft.name}
                                onChange={(e) => setVariantDraft({ ...variantDraft, name: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); handleSaveEditVariant(variant.id); }
                                  if (e.key === 'Escape') { handleCancelEditVariant(); }
                                }}
                                placeholder="Nombre de la variante"
                                className="rounded-xl flex-1"
                              />
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={variantDraft.price}
                                onChange={(e) => setVariantDraft({ ...variantDraft, price: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); handleSaveEditVariant(variant.id); }
                                  if (e.key === 'Escape') { handleCancelEditVariant(); }
                                }}
                                placeholder="Precio"
                                className="rounded-xl w-28"
                              />
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleSaveEditVariant(variant.id)}
                                className="rounded-xl bg-green-500 hover:bg-green-600"
                                title="Guardar variante"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEditVariant}
                                className="rounded-xl"
                                title="Cancelar"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 flex-1">
                                <span className="font-medium text-sm">{variant.name}</span>
                                <span className="text-orange-600 font-semibold">{formatPrice(variant.price)}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  variant.is_available 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {variant.is_available ? 'Disponible' : 'No disponible'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleStartEditVariant(variant)}
                                  className="text-orange-500 hover:text-orange-700"
                                  title="Editar nombre y precio"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleToggleVariantAvailability(variant.id)}
                                  className="text-blue-500 hover:text-blue-700 text-xs"
                                >
                                  {variant.is_available ? 'Deshabilitar' : 'Habilitar'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveVariant(variant.id)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    💡 Ejemplo: Alitas BBQ Grande ($400), Alitas Picantes Mediana ($300), etc.
                    {(!formData.variants || formData.variants.length === 0) && ' Si no agregas variantes, se usará el precio base.'}
                  </p>
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                  {loading ? 'Guardando...' : (editingProduct ? 'Actualizar Producto' : 'Crear Producto')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Buscador por nombre o código de barras */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre o código de barras…"
            className="rounded-xl pl-9"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Limpiar búsqueda"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Categories Section - Filter */}
      {categories.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Filtrar por Categoría</h3>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setSelectedCategoryFilter(null)}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                selectedCategoryFilter === null
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                  : 'bg-white/80 backdrop-blur-xl border border-orange-100 text-gray-800 hover:border-orange-300'
              }`}
            >
              Todas ({products.length})
            </button>
            {categories.map((cat) => {
              const count = products.filter(p => p.category_id === cat.id).length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategoryFilter(cat.id)}
                  className={`px-4 py-2 rounded-xl font-medium transition-all ${
                    selectedCategoryFilter === cat.id
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                      : 'bg-white/80 backdrop-blur-xl border border-orange-100 text-gray-800 hover:border-orange-300'
                  }`}
                >
                  {cat.name} ({count})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="flex flex-wrap gap-4 justify-center">
        {filteredProducts.map((product) => (
          <Card key={product.id} data-testid={`product-card-${product.id}`} className="w-[340px] h-[416px] overflow-hidden bg-white/80 backdrop-blur-xl border-orange-100 hover-lift flex flex-col">
            <div className="h-[200px] w-full overflow-hidden bg-gradient-to-br from-orange-100 to-red-100 flex-shrink-0">
              {product.image_url ? (
                <img 
                  src={getImageUrl(product.image_url)}
                  alt={product.name}
                  className="w-full h-full"
                  style={{ objectFit: 'contain' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-16 h-16 text-gray-300" />
                </div>
              )}
            </div>
            <div className="p-3 flex-1 flex flex-col overflow-hidden">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-bold text-gray-800">{product.name}</h3>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${product.is_available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {product.is_available ? 'Disponible' : 'No disponible'}
                </span>
              </div>
              {product.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
              )}
              
              {/* Mostrar variantes si existen */}
              {product.variants && product.variants.length > 0 ? (
                <div className="mb-4">
                  <div className="text-xs text-gray-500 mb-2">
                    {product.variants.length} variante(s):
                  </div>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {product.variants.slice(0, 3).map((variant) => (
                      <div key={variant.id} className="flex justify-between text-xs">
                        <span className="text-gray-700">{variant.name}</span>
                        <span className="font-semibold text-orange-600">${formatAmount(variant.price)}</span>
                      </div>
                    ))}
                    {product.variants.length > 3 && (
                      <div className="text-xs text-gray-400 italic">+{product.variants.length - 3} más...</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between mb-4">
                  <span className="text-2xl font-bold text-orange-600">${formatAmount(product.price)}</span>
                  <span className="text-xs text-gray-500">{product.preparation_time} min</span>
                </div>
              )}
              {product.tracked && (
                <div className="flex items-center justify-center gap-2 mb-3 text-xs">
                  <span className={`px-2 py-0.5 rounded-full font-semibold ${
                    Number(product.stock) <= 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {Number(product.stock) <= 0 ? 'Agotado' : `Stock: ${product.stock}`}
                  </span>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {/* Botón Habilitar/Deshabilitar */}
                <Button
                  onClick={() => toggleAvailability(product)}
                  variant="outline"
                  size="sm"
                  className={`flex-1 whitespace-nowrap rounded-xl ${
                    product.is_available
                      ? 'border-yellow-300 text-yellow-700 hover:bg-yellow-50'
                      : 'border-green-300 text-green-700 hover:bg-green-50'
                  }`}
                >
                  {product.is_available ? (
                    <><Ban className="w-4 h-4 mr-2" /> Deshabilitar</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" /> Habilitar</>
                  )}
                </Button>
                {/* Botón Editar - Solo si tiene permiso */}
                {hasPermission('products:edit') && (
                  <Button
                    onClick={() => handleEditProduct(product)}
                    variant="outline"
                    size="sm"
                    data-testid={`edit-product-${product.id}`}
                    className="flex-1 whitespace-nowrap border-orange-200 hover:bg-orange-50 rounded-xl"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                )}
                
                {/* Botón Eliminar - Solo si tiene permiso */}
                {hasPermission('products:delete') && (
                  <Button
                    onClick={() => handleDeleteProduct(product.id)}
                    variant="outline"
                    size="sm"
                    data-testid={`delete-product-${product.id}`}
                    className="flex-1 whitespace-nowrap border-red-200 hover:bg-red-50 text-red-600 rounded-xl"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {filteredProducts.length === 0 && products.length > 0 && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">
            {searchTerm
              ? `No se encontraron productos para "${searchTerm}"`
              : 'No hay productos en esta categoría'}
          </p>
          <Button onClick={() => { setSelectedCategoryFilter(null); setSearchTerm(''); }} variant="outline" className="border-orange-200 rounded-xl">
            Ver Todos los Productos
          </Button>
        </div>
      )}

      {products.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No hay productos creados</p>
          <Button onClick={() => setIsDialogOpen(true)} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
            <Plus className="w-4 h-4 mr-2" />
            Crear Primer Producto
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductsTab;
